/**
 * We skip writing of these categories to audit data store
 *
 * @type {Set}
 */
const doNotWriteCategories = new Set([
  'application'
]);

/**
 * We are not going to require authenticated client request to log the following category / action  combos.
 * We would expect all other actions to take place during an authenticated user session.
 * We use this information in order to fail requests against the service that are not taking place during authenticated session.
 *
 * @type {Object.<string, Set>}
 */
const actionsNotRequiringAuthentication = {
  application: new Set([ 'error' ]),
  authentication: new Set([
    'check completed',
    'failed',
    'logout completed',
    'password forgot completed',
    'password forgot failed',
    'rejected'
  ]),
  'help video': new Set(['query failed']),
  navigation: new Set([
    'completed',
    'failed'
  ])
};

const getActionMetadata = (category, action) => {
  const meta = {
    requiresAuthentication: true,
    writeToStore: true
  };

  if (
    actionsNotRequiringAuthentication[category] &&
    actionsNotRequiringAuthentication[category].has(action)
  ) {
    meta.requiresAuthentication = false;
  }

  if (doNotWriteCategories.has(category)) {
    meta.writeToStore = false;
  }

  return meta;
};

class AuditEvent {
  constructor(action, context) {
    if (!action) {
      throw new Error('action must be provided to AuditEvent constructor');
    }

    if (!context) {
      throw new Error('context must be provided to AuditEvent constructor');
    }

    const actionInfo = AuditEvent.parseActionString(action);

    this.schema = {
      action: context.actionSchema,
      event: AuditEvent.schemaVersion
    };
    this.category = actionInfo.category;
    this.action = actionInfo.action;

    Object.assign(this, context);

    this.timestamp = new Date();
  }

  static parseActionString(actionString) {
    if (!/^[A-Z_]+__[A-Z_]+$/.test(actionString)) {
      throw new Error('invalid action string format');
    }

    const actionParts = actionString.split('__').map(
      (part) => (part || '').toLowerCase().replace(/_/g, ' ')
    );

    return {
      category: actionParts[0],
      action: actionParts[1]
    };
  }


  static validate(event, userIsAuthenticated = false) {
    const { category, action, clientKey, user, timestamp, schema } = event;

    if (!category || !action || !timestamp || !schema) {
      return false;
    }

    const meta = getActionMetadata(category, action);

    // we should never get a request to validate an event in category that should not be written
    if (!meta.writeToStore) {
      return false;
    }

    if (!meta.requiresAuthentication) {
      return true;
    }

    return (clientKey && user && userIsAuthenticated);
  }
}

/**
 * This schema version must be incremented if there are any breaking changes to ActionEvent object schema.
 * A breaking change would occur if there has been a change any existing object property names of property value types.
 * Adding new property names (at either top level or nested in property values) would not constitute a breaking change.
 *
 * @type {number}
 */
AuditEvent.schemaVersion = 1;

export default AuditEvent;



// WEBPACK FOOTER //
// ./src/model/AuditEvent.js